import {
  Box,
  Divider,
  Drawer,
  GlobalStyles,
  List,
  ListItemButton,
  Typography,
} from '@mui/material'
import { createRef, useCallback, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  DmRoomDetailQuery,
  DmRoomMessageBoxFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts/BaseLayout'
import { Head } from '@/components/layouts/Head'
import { ReplyFormValues } from '@/components/organisms'
import {
  DmRoomMessageList,
  DmRoomMessageListRef,
} from '@/components/organisms/dmRoom'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'
import { usePrivateAction } from '@/lib/privateAction'

export type State = {
  loading: boolean
  userId: string
  dmRoom: DmRoomDetailQuery['dmRoom']
  messages: DmRoomMessageBoxFragment[]
  hasNextPage: boolean
}

export type Action = {
  onReply: (inputs: ReplyFormValues) => Promise<void>
  onFetchMore: () => Promise<void>
  onReportUser: (
    contentId: string,
    userId: string,
    accountId: string
  ) => Promise<void>
  onBlockDm: (userId: string) => Promise<void>
}

export type DmRoomDetailRef = DmRoomMessageListRef

export const dmRoomDetailRef = createRef<DmRoomDetailRef>()

export const DmRoomDetailTemplate: Template<State, Action> = ({
  state,
  onReply,
  onFetchMore,
  onReportUser,
  onBlockDm,
}) => {
  const { routerNames } = useRouter()
  const { t } = useTranslation('dmRoomDetail')
  const { handleLoginModal } = usePrivateAction()
  const [open, setOpen] = useState<boolean>(false)

  const toggleDrawer = useCallback(
    (open: boolean) => () => {
      setOpen(open)
    },
    []
  )

  const handleReportUser = useCallback(async () => {
    await onReportUser(
      state.dmRoom?.id ?? '',
      state.dmRoom?.artist?.id ?? '',
      state.dmRoom?.artist?.accountId ?? ''
    )
    setOpen(false)
  }, [onReportUser, state.dmRoom])

  const handleBlockDm = useCallback(async () => {
    await onBlockDm(state.dmRoom?.artist?.id ?? '')
    setOpen(false)
  }, [onBlockDm, state.dmRoom])

  return (
    <ArtistThemeProvider artistId={state.dmRoom?.artist?.id}>
      {/**
       * @description disabled scroll
       */}
      <GlobalStyles styles={{ body: { overflow: 'hidden' } }} />
      <Head title={routerNames.DmRoomDetail} />
      <BaseLayout
        margin
        header={{
          title: state.dmRoom?.title ?? '',
          back: true,
          right: state.dmRoom ? (
            <IconButton.More
              bgcolor="transparent"
              color="grey.700"
              size="medium"
              onClick={toggleDrawer(true)}
            />
          ) : undefined,
        }}
        navigation={{
          type: 'replyForm',
          props: {
            onReply: async values => onReply(values),
            visibleImage: true,
            userId: '',
            singleInputMode: true,
          },
        }}
        sx={{
          pb: styles.safeAreaBottom,
        }}
      >
        <DmRoomMessageList
          userId={state.userId}
          messages={state.messages}
          dmRoom={state.dmRoom}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          emptyMessage={t('empty')}
          refObject={dmRoomDetailRef}
        />
      </BaseLayout>
      <Drawer disableEnforceFocus open={open} onClose={toggleDrawer(false)}>
        <Box sx={{ p: 5 }}>
          <Typography variant="h6" color="grey.600" align="center">
            {state.dmRoom?.artist?.displayName}
          </Typography>
        </Box>
        <Divider />
        <List>
          <ListItemButton
            component={Box}
            sx={{ height: 52 }}
            onClick={handleLoginModal(handleReportUser)}
          >
            <Typography variant="h6">{t('report')}</Typography>
          </ListItemButton>
          <ListItemButton
            component={Box}
            sx={{ height: 52 }}
            divider
            onClick={handleLoginModal(handleBlockDm)}
          >
            <Typography variant="h6">{t('block')}</Typography>
          </ListItemButton>
          <ListItemButton
            component={Box}
            sx={{ height: 52 }}
            alignItems="center"
            onClick={toggleDrawer(false)}
          >
            <Typography variant="h6" align="center" sx={{ width: '100%' }}>
              {t('cancel')}
            </Typography>
          </ListItemButton>
        </List>
      </Drawer>
    </ArtistThemeProvider>
  )
}
